import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { ExternalLink } from '@/atoms/ExternalLink'
import { InternalLink } from '@/atoms/InternalLink'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { LabelSM, ParagraphMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { NotificationsMenu } from '@/molecules/Notifications'
import { RegionSelector } from '@/molecules/RegionSelector'
import { OmniboxSearchInput } from '@/organisms/Search'
import { MobileNav } from '@/organisms/SiteNav/MobileSiteNav/MobileNav'
import { useSiteNavContext } from '@/organisms/SiteNav/SiteNavContext'
import { UserMenu } from '@/organisms/SiteNav/UserMenu'
import { useGuildMember } from '@/services/GuildUserService'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { ANGXStockTicker } from '../ANGXStockTicker'

// eslint-disable-next-line sonarjs/cognitive-complexity
export const MobileSiteNav: React.FC = () => {
  const { t } = useTranslate('common')
  const { isLoggedIn, loading: loadingUser } = useUser()
  const { isGuildMember, loading: loadingGuild } = useGuildMember()
  const track = useSafeTrack()
  const router = useSafeRouter()
  const { pathname, asPath } = router || {}

  const normalizedPath = useMemo(() => {
    const raw = asPath || pathname || ''
    const withoutQuery = raw.split('?')[0] || ''
    const withoutHash = withoutQuery.split('#')[0] || ''
    const withoutLocale = withoutHash.replace(/^\/(?:[a-zA-Z]{2}(?:-[a-zA-Z]{2})?)(?=\/|$)/, '')
    const normalized = withoutLocale === '' ? '/' : withoutLocale
    return normalized === '/' ? '/' : normalized.replace(/\/$/, '')
  }, [asPath, pathname])

  const isInNonGiftGuildPath = useMemo(() => {
    const disallowedWildcardPaths = ['/guild/gift']
    return !disallowedWildcardPaths.some((path) => normalizedPath.startsWith(path))
  }, [normalizedPath])

  const isInShowJoinGuildPath = useMemo(() => {
    const allowedPaths = ['/', '/home', '/watch', '/account', '/blog', '/guild/voting', '/guild/impact']
    const allowedWildcards = ['/watch/', '/account/', '/category/']
    return allowedPaths.includes(normalizedPath) || allowedWildcards.some((prefix) => normalizedPath.startsWith(prefix))
  }, [normalizedPath])

  const showStockTicker = useMemo(() => {
    // Prefer asPath (captures resolved dynamic locale segment); fallback to pathname
    const raw = asPath || pathname || ''
    // Strip query then hash fragments defensively
    const firstSplit = (raw || '').split('?')[0]
    const secondSplit = (firstSplit || '').split('#')[0]
    const pathOnly = secondSplit || '/'
    // Remove a leading locale folder (e.g. /en, /es, /en-US)
    const withoutLocale = pathOnly.replace(/^\/(?:[a-zA-Z]{2}(?:-[a-zA-Z]{2})?)(?=\/|$)/, '')
    const normalized = withoutLocale === '' ? '/' : withoutLocale
    // Do not strip the only slash on root; only trim trailing slash for longer paths
    const cleaned = normalized === '/' ? '/' : normalized.replace(/\/$/, '')
    const isRoot = cleaned === '/' || cleaned === ''
    return isRoot || cleaned === '/home' || cleaned === '/watch'
  }, [asPath, pathname])

  const handleUpgradeClick = useCallback(() => {
    track('Nav Upgrade CTA Clicked')
  }, [track])

  const {
    closeMobileMenu,
    fetchOnlyNew,
    handleCloseNavMenu,
    handleOpenLanguageModal,
    handleOpenNotificationMenu,
    handleOpenRegionMenu,
    handleOpenRegionModal,
    handleCloseLanguageModal,
    hideMobileNavigation,
    hideNavigation,
    isMobileMenuOpen,
    isNotificationsMenuOpen,
    isRegionsMenuOpen,
    languagesEnabled,
    notificationsCount,
    regions,
    shouldUseDarkMode,
    showLanguageAndHelpLinks,
    showNotifications,
    toggleMobileMenu,
  } = useSiteNavContext()

  const handleNavShopClick = useCallback(() => {
    track('Nav Shop Clicked')
    closeMobileMenu()
  }, [closeMobileMenu, track])

  const handleGiftGuildClick = useCallback(() => {
    track('Gift Guild CTA Clicked', { location: 'mobile_sitenav', isGuildMember })
    closeMobileMenu()
  }, [track, closeMobileMenu, isGuildMember])

  const isShowGiftGuildCta = isInNonGiftGuildPath && !loadingGuild && isGuildMember

  const showJoinGuildButton = isInShowJoinGuildPath && !loadingUser && !loadingGuild && (!isLoggedIn || !isGuildMember)

  return (
    <div
      className={classNames(
        'flex xl:hidden flex-row justify-between items-center z-50 py-3 px-4 h-16',
        hideMobileNavigation && 'hidden',
      )}
    >
      {/* Navigation */}
      <When condition={!hideNavigation}>
        <div className="flex w-32 flex-row items-center justify-start gap-1">
          <div className={classNames('relative w-8 h-6')}>
            <input
              type="checkbox"
              id="mobile-menu-toggle"
              className="hidden"
              checked={isMobileMenuOpen}
              onChange={toggleMobileMenu}
            />
            <label
              htmlFor="mobile-menu-toggle"
              className={classNames('icon cursor-pointer', shouldUseDarkMode ? 'icon-dark' : 'icon-light')}
            >
              <span />
            </label>

            <label
              htmlFor="mobile-menu-toggle"
              className={classNames(
                'overlay -z-40  w-screen fixed top-0 left-0 bottom-0 right-0 transition-height duration-300',
                shouldUseDarkMode ? 'bg-core-gray-950' : 'bg-white',
                isMobileMenuOpen ? 'h-screen' : 'h-0',
              )}
            />

            <nav
              aria-labelledby="mobile-nav-title"
              className={classNames(
                'relative z-50 -ml-4 mt-2 w-screen overflow-y-scroll lg:overflow-y-auto overflow-x-hidden transition-height duration-200',
                isMobileMenuOpen ? 'h-[95vh]' : 'h-0',
              )}
            >
              <h2 id="mobile-nav-title" className="sr-only">
                <Translate t={t} i18nKey="mobileMenu">
                  Mobile Menu
                </Translate>
              </h2>

              {isShowGiftGuildCta && (
                <LinkButton
                  href={paths.guild.gift.index}
                  variant="white"
                  onClick={handleGiftGuildClick}
                  className="mx-4 mt-6 max-h-8 rounded-lg !border-[#FDA21E] !bg-[#FDA21E] px-4 py-2 md:max-w-[320px]"
                >
                  <LabelSM weight="semibold" color="core-gray-950" className="text-xs">
                    {t('giftTheGuild', 'Gift the Guild')}
                  </LabelSM>
                </LinkButton>
              )}

              {showJoinGuildButton && (
                <LinkButton
                  href={paths.guild.pricing}
                  variant="white"
                  onClick={handleUpgradeClick}
                  className="mx-4 mt-6 max-h-8 rounded-lg !border-[#FDA21E] !bg-[#FDA21E] px-4 py-2 md:max-w-[320px]"
                >
                  <LabelSM weight="semibold" color="core-gray-950" className="text-xs">
                    {t('upgrade', 'Upgrade')}
                  </LabelSM>
                </LinkButton>
              )}

              {showStockTicker && <ANGXStockTicker />}
              <When condition={isLoggedIn}>
                <MobileNav.Modal>
                  <MobileNav.Menu
                    label={t('watch', 'Watch')}
                    href={paths.watch.index}
                    Element={InternalLink}
                    onClick={closeMobileMenu}
                    id="watch-menu"
                  >
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('comedy', 'Comedy')}
                      href={paths.category.comedyTvShows}
                      id="comedy-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('drama', 'Drama')}
                      href={paths.category.drama}
                      id="drama-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('documentary', 'Documentary')}
                      href={paths.category.documentaries}
                      id="documentary-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('family', 'Family')}
                      href={paths.category.family}
                      id="family-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('free', 'Free')}
                      href={paths.category.free}
                      id="free-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('sports', 'Sports')}
                      href={paths.category.sports}
                      id="sports-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('faithBased', 'Faith Based')}
                      href={paths.category.faithBased}
                      id="faith-based-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                  </MobileNav.Menu>
                  <div className="relative w-fit">
                    <MobileNav.Menu
                      label={t('menuLinkNewUpcoming', 'New & Upcoming')}
                      href={paths.category.newReleases}
                      Element={InternalLink}
                      onClick={closeMobileMenu}
                      id="new-upcoming-menu"
                    >
                      <MobileNav.Item
                        Element={InternalLink}
                        label={t('menuLinkNewReleases', 'New Releases')}
                        href={paths.category.newReleases}
                        onClick={closeMobileMenu}
                        id="new-releases"
                      />
                      <MobileNav.Item
                        Element={InternalLink}
                        label={t('comingSoon', 'Coming Soon')}
                        href={paths.comingSoon.index}
                        onClick={closeMobileMenu}
                        id="coming-soon"
                      />
                    </MobileNav.Menu>
                  </div>
                  <div className="relative w-fit">
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('inTheaters', 'In Theaters')}
                      href={paths.tickets.index}
                      onClick={closeMobileMenu}
                      id="in-theaters"
                    />
                  </div>
                  <MobileNav.Menu
                    label={t('guild', 'Guild')}
                    href={
                      isGuildMember
                        ? `${paths.guild.index}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                        : `${paths.guild.join}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                    }
                    Element={InternalLink}
                    onClick={closeMobileMenu}
                    id="guild-menu"
                  >
                    <MobileNav.Item
                      Element={InternalLink}
                      label={isGuildMember ? t('myBenefits', 'My Benefits') : t('learnMore', 'Learn More')}
                      href={
                        isGuildMember
                          ? `${paths.guild.index}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                          : `${paths.guild.join}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                      }
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                      id="guild-dashboard-join"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('filmmakers', 'Filmmakers')}
                      href={paths.filmmakers.index}
                      onClick={closeMobileMenu}
                      id="filmmakers"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('angelImpact', 'Angel Impact')}
                      href={paths.guild.impact}
                      onClick={closeMobileMenu}
                      id="guild-impact"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('guildVoting', 'Guild Voting')}
                      href={paths.guild.voting}
                      onClick={closeMobileMenu}
                      id="cast-your-vote"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('giftTheGuild', 'Gift the Guild')}
                      href={isGuildMember ? paths.guild.gift.index : paths.external.giftGuild}
                      onClick={handleGiftGuildClick}
                      id="gift-the-guild"
                    />
                  </MobileNav.Menu>
                  <MobileNav.Item
                    Element={ExternalLink}
                    label={t('menuLinkShop', 'Shop')}
                    href={paths.external.shop.index}
                    id="shop"
                    onClick={handleNavShopClick}
                  />
                  <MobileNav.Item
                    Element={InternalLink}
                    label={t('blog', 'Blog')}
                    href={paths.blog.index}
                    id="blog"
                    onClick={closeMobileMenu}
                  />
                  <hr
                    className={classNames(
                      'mx-2 my-1.5',
                      shouldUseDarkMode ? 'border-t-core-gray-800' : 'border-core-gray-200',
                    )}
                  />
                  {!showLanguageAndHelpLinks && (
                    <>
                      {languagesEnabled && (
                        <div
                          onClick={() => {
                            handleOpenLanguageModal()
                            closeMobileMenu()
                          }}
                          className={classNames(
                            'whitespace-nowrap block',
                            'rounded-md px-2 py-1.5 cursor-pointer bg-transparent transition-bg duration-200',
                            shouldUseDarkMode ? 'hover:bg-[rgb(255,255,255,0.1)]' : 'hover:bg-[rgb(0,0,0,0.06)]',
                          )}
                        >
                          <ParagraphMD color={shouldUseDarkMode ? 'white' : 'black'}>
                            {t('languages', 'Languages')}
                          </ParagraphMD>
                        </div>
                      )}
                      <MobileNav.Item
                        Element={ExternalLink}
                        label={t('help', 'Help')}
                        href={paths.external.zendesk.support}
                        onClick={closeMobileMenu}
                        id="help"
                      />
                    </>
                  )}
                </MobileNav.Modal>
              </When>
              <When condition={!isLoggedIn}>
                <MobileNav.Modal>
                  <MobileNav.Menu
                    label={t('watch', 'Watch')}
                    href={paths.watch.index}
                    Element={InternalLink}
                    onClick={closeMobileMenu}
                    id="watch-menu"
                  >
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('comedy', 'Comedy')}
                      href={paths.category.comedyTvShows}
                      id="comedy-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('drama', 'Drama')}
                      href={paths.category.drama}
                      id="drama-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('documentary', 'Documentary')}
                      href={paths.category.documentaries}
                      id="documentary-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('family', 'Family')}
                      href={paths.category.family}
                      id="family-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('free', 'Free')}
                      href={paths.category.free}
                      id="free-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('sports', 'Sports')}
                      href={paths.category.sports}
                      id="sports-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('faithBased', 'Faith Based')}
                      href={paths.category.faithBased}
                      id="faith-based-category"
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                    />
                  </MobileNav.Menu>
                  <div className="relative w-fit">
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('inTheaters', 'In Theaters')}
                      href={paths.tickets.index}
                      onClick={closeMobileMenu}
                      id="in-theaters"
                    />
                  </div>
                  <MobileNav.Menu
                    label={t('guild', 'Guild')}
                    href={
                      isGuildMember
                        ? `${paths.guild.index}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                        : `${paths.guild.join}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                    }
                    Element={InternalLink}
                    onClick={closeMobileMenu}
                    id="guild-menu"
                  >
                    <MobileNav.Item
                      Element={InternalLink}
                      label={isGuildMember ? t('myBenefits', 'My Benefits') : t('learnMore', 'Learn More')}
                      href={
                        isGuildMember
                          ? `${paths.guild.index}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                          : `${paths.guild.join}?utm_medium=web&utm_campaign=guild-memberships&utm_source=sitenav`
                      }
                      onClick={closeMobileMenu}
                      linkContext="menu-link"
                      id="guild-dashboard-join"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('filmmakers', 'Filmmakers')}
                      href={paths.filmmakers.index}
                      onClick={closeMobileMenu}
                      id="filmmakers"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('angelImpact', 'Angel Impact')}
                      href={paths.guild.impact}
                      onClick={closeMobileMenu}
                      id="guild-impact"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('guildVoting', 'Guild Voting')}
                      href={paths.guild.voting}
                      onClick={closeMobileMenu}
                      id="cast-your-vote"
                    />
                    <MobileNav.Item
                      Element={InternalLink}
                      label={t('giftTheGuild', 'Gift the Guild')}
                      href={isGuildMember ? paths.guild.gift.index : paths.external.giftGuild}
                      onClick={handleGiftGuildClick}
                      id="gift-the-guild"
                    />
                  </MobileNav.Menu>
                  <hr
                    className={classNames(
                      'mx-2 my-1.5',
                      shouldUseDarkMode ? 'border-t-core-gray-800' : 'border-core-gray-200',
                    )}
                  />
                  {!showLanguageAndHelpLinks && (
                    <>
                      {languagesEnabled && (
                        <div
                          onClick={() => {
                            handleOpenLanguageModal()
                            closeMobileMenu()
                          }}
                          className={classNames(
                            'whitespace-nowrap block',
                            'rounded-md px-2 py-1.5 cursor-pointer bg-transparent transition-bg duration-200',
                            shouldUseDarkMode ? 'hover:bg-[rgb(255,255,255,0.1)]' : 'hover:bg-[rgb(0,0,0,0.06)]',
                          )}
                        >
                          <ParagraphMD color={shouldUseDarkMode ? 'white' : 'black'}>
                            {t('languages', 'Languages')}
                          </ParagraphMD>
                        </div>
                      )}
                      <MobileNav.Item
                        Element={ExternalLink}
                        label={t('help', 'Help')}
                        href={paths.external.zendesk.support}
                        onClick={closeMobileMenu}
                        id="help"
                      />
                    </>
                  )}
                </MobileNav.Modal>
              </When>
            </nav>
          </div>
          <OmniboxSearchInput />
          <div>
            {regions && regions?.length > 0 && (
              <RegionSelector
                onRegionModalOpen={handleOpenRegionModal}
                onLanguageModalClose={handleCloseLanguageModal}
                closeMobileMenu={closeMobileMenu}
                isMobileNavItem
                onClose={handleCloseNavMenu}
                onOpen={handleOpenRegionMenu}
                isOpen={isRegionsMenuOpen}
                isDarkMode={shouldUseDarkMode}
                regions={regions}
              />
            )}
          </div>
        </div>
      </When>

      {/* Angel Logo */}
      <div className="flex shrink-0 items-center">
        <LogoLink closeMobileMenu={closeMobileMenu} shouldUseDarkMode={shouldUseDarkMode} isLoggedIn={isLoggedIn} />
      </div>

      {/* User Nav */}
      <div className="flex w-32 flex-row items-center justify-end">
        {showNotifications && (
          <NotificationsMenu
            notificationsCount={notificationsCount}
            fetchOnlyNew={fetchOnlyNew}
            isOpen={isNotificationsMenuOpen}
            onClose={handleCloseNavMenu}
            onOpen={handleOpenNotificationMenu}
            isDarkMode={shouldUseDarkMode}
          />
        )}

        <UserMenu onClick={closeMobileMenu} loginButtonVariant={shouldUseDarkMode ? 'white' : 'black'} />
      </div>
    </div>
  )
}

function LogoLink(props: { shouldUseDarkMode: boolean; closeMobileMenu: () => void; isLoggedIn: boolean }) {
  return (
    <InternalLink
      aria-label="Angel"
      href={props.isLoggedIn ? paths.watch.index : paths.index}
      onClick={props.closeMobileMenu}
    >
      <When condition={props.shouldUseDarkMode}>
        <AngelLogo color="white" />
      </When>
      <When condition={!props.shouldUseDarkMode}>
        <AngelLogo color="black" />
      </When>
    </InternalLink>
  )
}
